import { useEditProgramDetails } from './hooks';
import { Form, FormProvider } from 'react-hook-form';
import { Box, Button, Tab } from '@mui/material';
import { History, EditProgramFields, ConsentTab } from './components';
import { CustomModal, SaveChangesModal, StatusBanner } from '@components';
import { t } from 'i18next';
import { theme } from '@store';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import dayjs from 'dayjs';
import { StatusChangeConfirmationModal } from '@views/pages/Patient/components/DetailsCard/components/StatusChangeConfirmationModal';

interface IEditProgramDetailsProps {
  patient_id: string;
  program_id: string;
  programName: string;
  disabled: boolean;
  patient_name: string;
  patient_birthdate: string;
  isOpen: boolean;
  handleClose: () => void;
}

export const EditProgramDetails = ({
  patient_id,
  program_id,
  programName,
  patient_name,
  patient_birthdate,
  disabled = false,
  isOpen,
  handleClose,
}: IEditProgramDetailsProps) => {
  const {
    formMethods,
    isSubmitting,
    onSubmit,
    programStatusOptions,
    unalignmentReasons,
    ineligibilityReasons,
    tierOptions,
    history,
    programDetails,
    currentStatus,
    isSaveChangesModalOpen,
    handleCloseSaveChangesModal,
    handleDiscardChanges,
    handleSaveChanges,
    isValid,
    isEditSubmitting,
    tabIndex,
    handleTabChange,
    isConfirmModalOpen,
    setIsConfirmModalOpen,
    isProgramDetailsPending,
    consent,
    handleRequestClose,
    isConsentSaveModalOpen,
    handleConsentSave,
    handleConsentDiscard,
    handleConsentModalClose,
  } = useEditProgramDetails({
    patient_id,
    program_id,
    programName,
    handleClose,
  });

  const { control, watch } = formMethods;
  const alignmentOption = watch('new_status');

  const warningSubText =
    programName === 'GUIDE'
      ? alignmentOption === 'Unaligned'
        ? t(
            'patient.detailsCard.editPatientProgram.confirmationModal.guideUnalignedWarningSubText',
            { program: programName },
          )
        : t(
            'patient.detailsCard.editPatientProgram.confirmationModal.toBeUnalignedWarningSubText',
            { program: programName },
          )
      : t(
          'patient.detailsCard.editPatientProgram.confirmationModal.unalignedWarningSubText',
          { program: programName },
        );

  return (
    <>
      <CustomModal
        isOpen={isOpen}
        onClose={handleRequestClose}
        isLoading={isSubmitting || isProgramDetailsPending}
        title={t('patient.detailsCard.editPatientProgram.title')}
        subTitle={programName}
        disableOverflow
      >
        <Box>
          {/*help to hide modal when confirmation modal is open*/}
          {!isConfirmModalOpen && (
            <StatusBanner
              text={programDetails?.header_message || ''}
              variant={programDetails?.header_type || 'info'}
            />
          )}
        </Box>
        <TabContext value={tabIndex}>
          <TabList onChange={handleTabChange}>
            <Tab
              label={t('patient.detailsCard.editPatientProgram.statusHistory')}
              value='1'
            />
            <Tab
              label={t('patient.detailsCard.editPatientProgram.currentStatus')}
              value='2'
              disabled={programStatusOptions.length === 0 || disabled}
            />
            <Tab
              label={t(
                'patient.detailsCard.editPatientProgram.consent.tabLabel',
              )}
              value='3'
            />
          </TabList>

          <TabPanel value='1'>
            <Box sx={{ position: 'relative' }}>
              <Box
                sx={{
                  maxHeight: 'calc(100vh - 500px)',
                  overflowY: 'auto',
                }}
              >
                <History history={history} />
              </Box>
              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: '8px',
                  paddingY: 3,
                  backgroundColor: theme.palette.common.white,
                  position: 'sticky',
                  bottom: 0,
                  left: 0,
                }}
              >
                <Button
                  type='submit'
                  variant='contained'
                  color='secondary'
                  onClick={handleClose}
                  sx={{
                    fontSize: '0,875rem',
                    fontWeight: 'medium',
                    lineHeight: '24px',
                    color: 'white',
                  }}
                >
                  {t('general.close')}
                </Button>
              </Box>
            </Box>
          </TabPanel>
          <TabPanel value='2'>
            <FormProvider {...formMethods}>
              <Form
                control={control}
                onSubmit={e => onSubmit(e.event)}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '16px',
                  maxHeight: 'calc(100vh - 450px)',
                  overflowY: 'auto',
                  paddingTop: 8,
                }}
              >
                {programStatusOptions.length > 0 && (
                  <EditProgramFields
                    alignmentOption={alignmentOption}
                    tierOptions={tierOptions}
                    programStatusOptions={programStatusOptions}
                    unalignmentReasons={unalignmentReasons}
                    ineligibilityReasons={ineligibilityReasons}
                    currentStatus={currentStatus}
                    disabled={disabled}
                    programName={programName}
                  />
                )}

                <Box
                  sx={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: '8px',
                    paddingY: 3,
                    backgroundColor: theme.palette.common.white,
                    zIndex: 9,
                    position: 'sticky',
                    bottom: 0,
                    left: 0,
                  }}
                >
                  {programStatusOptions.length > 0 && (
                    <Button
                      type='submit'
                      variant='contained'
                      color='secondary'
                      sx={{
                        fontSize: '0,875rem',
                        fontWeight: 'medium',
                        lineHeight: '24px',
                        color: 'white',
                      }}
                      disabled={isSubmitting || disabled || !isValid}
                    >
                      {t('general.confirm')}
                    </Button>
                  )}
                </Box>
                <SaveChangesModal
                  isOpen={isSaveChangesModalOpen}
                  onClose={handleCloseSaveChangesModal}
                  isValid={isValid}
                  handleSaveChanges={handleSaveChanges}
                  discardChanges={handleDiscardChanges}
                  isLoading={isEditSubmitting}
                />
                <StatusChangeConfirmationModal
                  name={patient_name}
                  date={dayjs(patient_birthdate).format('MMM DD, YYYY')}
                  isOpen={isConfirmModalOpen}
                  onClose={() => setIsConfirmModalOpen(false)}
                  handleSaveChanges={handleSaveChanges}
                  isLoading={isEditSubmitting}
                  title={t(
                    'patient.detailsCard.editPatientProgram.confirmationModal.title',
                    { program: programName },
                  )}
                  warningText={t(
                    'patient.detailsCard.editPatientProgram.confirmationModal.warningText',
                  )}
                  warningSubText={warningSubText}
                  cancelActionText={t(
                    'patient.detailsCard.editPatientProgram.confirmationModal.cancelActionText',
                  )}
                  confirmActionText={t(
                    'patient.detailsCard.editPatientProgram.confirmationModal.confirmActionText',
                  )}
                />
              </Form>
            </FormProvider>
          </TabPanel>
          <TabPanel value='3'>
            <ConsentTab
              consent={consent}
              programName={programName}
              onClose={handleRequestClose}
              disabled={disabled}
            />
          </TabPanel>
        </TabContext>
        <SaveChangesModal
          isOpen={isConsentSaveModalOpen}
          onClose={handleConsentModalClose}
          isValid={true}
          handleSaveChanges={handleConsentSave}
          discardChanges={handleConsentDiscard}
          isLoading={consent.isSubmitting}
        />
      </CustomModal>
    </>
  );
};
